import { createI18n } from 'vue-i18n'
import { de, en, defaultLocale, type Locale } from '@/locales'

/**
 * Builds a fresh i18n instance.
 *
 * This used to be a module-scope singleton created once at import time. One
 * Node process serves every SSR request, so a per-request locale written onto
 * that singleton would leak into whatever request happened to render next: two
 * concurrent visitors could be served each other's language, non
 * deterministically. Locale is per request, so the instance has to be too.
 *
 * Components must use useI18n() from vue-i18n, which resolves the instance the
 * app was actually created with. The accessor below exists only for the
 * handful of modules that have no setup scope.
 */
/**
 * Named datetime formats for `d()`.
 *
 * Without these, `d(date, 'short')` has no format to resolve and renders an
 * empty string, which is silent: "Am {date} enden die Limits" simply reads
 * "Am  enden die Limits" and every session, role and sub-account date shows
 * blank. Any format name used in a template has to exist here.
 */
const datetimeFormats = {
  de: {
    short: { year: 'numeric', month: '2-digit', day: '2-digit' } as const
  },
  en: {
    short: { year: 'numeric', month: '2-digit', day: '2-digit' } as const
  }
}

export function createAppI18n(locale: Locale = defaultLocale) {
  return createI18n({
    legacy: false,
    locale,
    fallbackLocale: defaultLocale,
    messages: { de, en },
    datetimeFormats
  })
}

/**
 * The i18n instance the app was actually mounted with.
 *
 * Mirrors setActiveRouter in @/router, for the same reason and with the same
 * contract. Deliberately client-only: on the server every request builds its
 * own instance, and a module-level slot would hand one request's translations
 * to the next. Anything that runs during SSR must use useI18n() instead, which
 * is per-request safe because it resolves through the app's own injection.
 */
type AppI18n = ReturnType<typeof createAppI18n>

let activeI18n: AppI18n | null = null

export function setActiveI18n(i18n: AppI18n) {
  activeI18n = i18n
}

/** Returns null during SSR and before the app is created. Callers must handle it. */
export function getActiveI18n(): AppI18n | null {
  return activeI18n
}

/**
 * Translate from outside setup scope. Falls back to the key itself when no
 * app has been mounted yet, or during SSR, so a caller can never crash a
 * render by reaching for a translation that is not there.
 */
export function translate(key: string): string {
  const i18n = getActiveI18n()
  if (!i18n) return key
  return i18n.global.t(key)
}
